<script setup lang="ts">
import { useCoreStore } from '@apps/main/new-desktop/core/stores';

import {
  AppFullscreenMasonryGallery,
  AppFullscreenSingleImageGallery,
} from '@libs/ui';

import type {
  HotelDetailsFullscreenMasonryGalleryData,
  HotelDetailsGalleryData,
} from '../models';
import { HotelDetailsGalleryType } from '../models';
import { useHotelDetailsGalleryStore } from '../stores';

interface HotelDetailsGalleryProps {
  config: HotelDetailsGalleryData;
}
const props = defineProps<HotelDetailsGalleryProps>();

interface HotelDetailsGalleryEmits {
  (event: 'close'): void;
  (
    event: 'image-clicked',
    data: HotelDetailsFullscreenMasonryGalleryData
  ): void;
}
const emit = defineEmits<HotelDetailsGalleryEmits>();

const hotelDetailsGalleryStore = useHotelDetailsGalleryStore();
const coreStore = useCoreStore();
</script>

<template>
  <div class="hotel-details-gallery">
    <AppFullscreenMasonryGallery
      v-if="
        hotelDetailsGalleryStore.currentOpenedGallery !==
          HotelDetailsGalleryType.SingleImageForRoom && props.config.header
      "
      :image-urls="
        hotelDetailsGalleryStore.getImagesForFullScreenGallery(
          props.config.images
        )
      "
      :images-text="$t('planner.images')"
      :product-text="props.config.header"
      :close-gallery-aria-label="$t('a11y.close_image_gallery')"
      :error-image-handler="coreStore.handleImageLoadError"
      @close="emit('close')"
      @image-clicked="
        emit('image-clicked', {
          index: $event,
          images: props.config.images,
        })
      "
    />

    <AppFullscreenSingleImageGallery
      v-if="
        hotelDetailsGalleryStore.currentOpenedGallery !==
        HotelDetailsGalleryType.MasonryForHotel
      "
      :image-urls="
        hotelDetailsGalleryStore.getImagesForSingleImageGallery(
          props.config.images
        )
      "
      :initial-slide="hotelDetailsGalleryStore.currentSlideIndex"
      :left-arrow-aria-label="$t('a11y.previous_image')"
      :right-arrow-aria-label="$t('a11y.next_image')"
      :close-gallery-aria-label="$t('a11y.close_image_gallery')"
      :error-image-handler="coreStore.handleImageLoadError"
      @close="emit('close')"
    />
  </div>
</template>

<style scoped lang="scss">
.hotel-details-gallery {
  height: 100%;
  width: 100%;
  z-index: var(--panel-second-level-z-index);
  background-color: var(--color-white);
  position: relative;

  @include for-more-or-equal-than-1200-px {
    border-radius: var(--border-radius-12);
  }
}
</style>
